import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Nullable } from 'global';
import { Box, Stack } from '@mui/material';
import { formatCurrency } from 'utils';
import { logError } from 'utils/logger';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { formatPercent } from 'utils/formatters';
import { DoneAll, Send, Assignment } from '@mui/icons-material';
import { GET_PROPOSAL_METRICS } from 'modules/proposals/api';
import { DashboardFilters } from '../pages/DashboardFilters';
import { DashboardContent } from '../pages/DashboardContent';

type BestUsersTypes = 'highestMarkup' | 'mostProposalsSent' | 'highestAcceptedFlat' | 'highestAcceptedPercent';
type TotalTypes = 'sent' | 'created' | 'accepted';

interface BestUser {
  label: string;
  userFullName: Nullable<string>;
  value: Nullable<number | string>;
}

interface Total {
  label: string;
  amount: number;
  markup: string;
  activeDeals: number;
  icon: React.ReactNode;
}

export interface HighestResult {
  bestUsers: Record<BestUsersTypes, BestUser>;
  totals: {
    byCategory: Record<TotalTypes, Total>;
    createdByProposalsType: {
      active: { value: string; label: string };
      approved: { value: string; label: string };
      nonFunded: { value: string; label: string };
    };
  };
}

export interface ProposalByUser extends Record<string, string | number> {
  total: number;
  fullName: string;
}

export interface ProposalByLocation extends Record<string, string | number> {
  markup: number;
  locationName: string;
}

export interface PeriodVolume extends Record<string, string | number> {
  total: string;
  period: string;
}

export interface ProposalSummary extends Record<string, string | number> {
  fullName: string;
  markupSent: number;
  markupCreated: number;
  proposalsSent: number;
  markupAccepted: number;
  proposalsCreated: number;
  proposalsAccepted: number;
}

export interface MetricsVariables {
  input: Partial<{
    to: string;
    from: string;
    locationId: Nullable<string>;
    userProfileId: Nullable<string>;
    equipmentCondition: Nullable<string>;
    saleType: Nullable<string>;
  }>;
}

export const ProposalsMetricsDashboard = () => {
  const contactOptions = useSelector((state: any) =>
    state.account.contacts.map((contact: Record<string, any>) => ({
      label: contact.fullName,
      value: contact.id,
    }))
  );

  const [proposalByUser, setProposalByUser] = useState<ProposalByUser[]>([]);
  const [proposalByLocation, setProposalByLocation] = useState<ProposalByLocation[]>([]);
  const [dailyVolume, setDailyVolume] = useState<PeriodVolume[]>([]);
  const [weeklyVolume, setWeeklyVolume] = useState<PeriodVolume[]>([]);
  const [monthlyVolume, setMonthlyVolume] = useState<PeriodVolume[]>([]);
  const [proposalSummary, setProposalSummary] = useState<ProposalSummary[]>([]);
  const [summaryHighestResultData, setSummaryHighestResultData] = useState<HighestResult | null>(null);

  const {
    data: metricsData,
    refetch: getMetrics,
    loading: loadingMetrics,
  } = useQuery<any, MetricsVariables>(GET_PROPOSAL_METRICS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        to: moment().endOf('month').format('YYYY-MM-DD'),
        from: moment().startOf('month').format('YYYY-MM-DD'),
      },
    },
  });

  const handleFetchMetrics = async (variables: MetricsVariables['input']) => {
    try {
      await getMetrics({ input: variables });
    } catch (error) {
      logError(error);
    }
  };

  useEffect(() => {
    if (metricsData && metricsData.proposalMetrics && !loadingMetrics) {
      setProposalByUser(metricsData.proposalMetrics.created.byUser);
      setProposalByLocation(metricsData.proposalMetrics.accepted.markup.byLocation);
      setDailyVolume(metricsData.proposalMetrics.created.byDate);
      setWeeklyVolume(metricsData.proposalMetrics.created.byWeek);
      setMonthlyVolume(metricsData.proposalMetrics.created.byMonth);
      // We’ll build a mergedUsers object to gather counts and markups by user
      const mergedUsers: any = {};

      // Helper to make sure each user is initialized in mergedUsers
      const initUser = (fullName: string) => {
        if (!mergedUsers[fullName]) {
          mergedUsers[fullName] = {
            fullName,
            proposalsCreated: 0,
            proposalsSent: 0,
            proposalsAccepted: 0,
            markupCreated: 0,
            markupSent: 0,
            markupAccepted: 0,
          };
        }
      };

      // A small helper to safely read arrays (in case some part is missing)
      const getArray = (obj: any, path: any) => {
        // e.g. getArray(proposalMetrics, 'created.byUser') => returns array or []
        return path.split('.').reduce((acc: any, cur: any) => (acc && acc[cur]) || [], obj);
      };

      // Merge data for proposals "created", "sent", "accepted"
      // 1. Merge counts (byUser)
      // 2. Merge markup (markup.byUser)
      ['created', 'sent', 'accepted'].forEach(metric => {
        // 1) Merge the byUser totals
        getArray(metricsData.proposalMetrics, `${metric}.byUser`).forEach((item: any) => {
          initUser(item.fullName);
          if (metric === 'created') mergedUsers[item.fullName].proposalsCreated = item.total;
          if (metric === 'sent') mergedUsers[item.fullName].proposalsSent = item.total;
          if (metric === 'accepted') mergedUsers[item.fullName].proposalsAccepted = item.total;
        });

        // 2) Merge the markup byUser
        getArray(metricsData.proposalMetrics, `${metric}.markup.byUser`).forEach((item: any) => {
          initUser(item.fullName);
          if (metric === 'created') mergedUsers[item.fullName].markupCreated = item.markup || 0;
          if (metric === 'sent') mergedUsers[item.fullName].markupSent = item.markup || 0;
          if (metric === 'accepted') mergedUsers[item.fullName].markupAccepted = item.markup || 0;
        });
      });

      // Convert mergedUsers to an array
      setProposalSummary(Object.values(mergedUsers));

      setSummaryHighestResultData(
        calcHighestResult({
          totalSent: metricsData.proposalMetrics.sent.byUser as any,
          totalCreated: metricsData.proposalMetrics.created.byUser as any,
          totalAccepted: metricsData.proposalMetrics.accepted.byUser as any,
          markupByUser: metricsData.proposalMetrics.accepted.markup.byUser as any,
          markups: {
            sent: metricsData.proposalMetrics.sent.markup.byUser.reduce(
              (acc: any, value: any) => (acc += value.markup),
              0
            ),
            created: metricsData.proposalMetrics.created.markup.byUser.reduce(
              (acc: any, value: any) => (acc += value.markup),
              0
            ),
            accepted: metricsData.proposalMetrics.accepted.markup.byUser.reduce(
              (acc: any, value: any) => (acc += value.markup),
              0
            ),
          },
          deals: {
            sent: metricsData.proposalMetrics.sent.byUser.length,
            created: metricsData.proposalMetrics.created.byUser.length,
            accepted: metricsData.proposalMetrics.accepted.byUser.length,
          },
        })
      );
    }
  }, [metricsData, loadingMetrics]);

  return (
    <Stack gap="1rem" height="100%">
      <DashboardFilters contactsOptions={contactOptions} onFetchMetrics={handleFetchMetrics} />

      <DashboardContent
        isLoading={loadingMetrics}
        dailyVolume={dailyVolume}
        weeklyVolume={weeklyVolume}
        monthlyVolume={monthlyVolume}
        proposalByUser={proposalByUser}
        proposalSummary={proposalSummary}
        proposalByLocation={proposalByLocation}
        summaryHighestResultData={summaryHighestResultData}
      />
    </Stack>
  );
};

function calcHighestResult(options: {
  totalSent: any[];
  markupByUser: any[];
  totalCreated: any[];
  totalAccepted: any[];
  markups: {
    sent: number;
    created: number;
    accepted: number;
  };
  deals: {
    sent: number;
    created: number;
    accepted: number;
  };
}): HighestResult {
  const { totalSent, markupByUser, totalCreated, totalAccepted, markups, deals } = options;

  const sortByHighestTotal = ({ total: total1 }: any, { total: total2 }: any) => total2 - total1;

  const amountOfCreated = totalCreated.reduce((acc, { total }) => (acc += total), 0);
  const amountOfSent = totalSent.reduce((acc, { total }) => (acc += total), 0);
  const amountOfAccepted = totalAccepted.reduce((acc, { total }) => (acc += total), 0);

  const [userWithHighestSentAmount] = totalSent.toSorted(sortByHighestTotal);
  const [userWithHighestAcceptedAmount] = totalAccepted.toSorted(sortByHighestTotal);
  const [userWithHighestMarkup] = markupByUser.toSorted(
    ({ markup: markup1 }, { markup: markup2 }) => markup2 - markup1
  );

  const totalProposals = amountOfCreated + amountOfSent + amountOfAccepted;

  const activeDealsPercent = Number(((amountOfSent * 100) / totalProposals).toFixed(2));
  const approvedDealsPercent = Number(((amountOfAccepted * 100) / totalProposals).toFixed(2));
  const nonFundedDealsPercent = Number((((amountOfSent + amountOfCreated) * 100) / totalProposals).toFixed(2));

  return {
    totals: {
      byCategory: {
        created: {
          label: 'Created',
          amount: amountOfCreated,
          activeDeals: deals.created,
          icon: <Assignment color="primary" />,
          markup: formatCurrency(markups.created, true),
        },
        sent: {
          label: 'Sent',
          amount: amountOfSent,
          activeDeals: deals.sent,
          icon: <Send color="primary" />,
          markup: formatCurrency(markups.sent, true),
        },
        accepted: {
          label: 'Accepted',
          amount: amountOfAccepted,
          activeDeals: deals.accepted,
          icon: <DoneAll color="primary" />,
          markup: formatCurrency(markups.accepted, true),
        },
      },
      createdByProposalsType: {
        active: {
          label: '% Created (of Active Deals)',
          value: isNaN(activeDealsPercent) ? '-' : `${activeDealsPercent}%`,
        },
        approved: {
          label: '% Created (of Approved Deals)',
          value: isNaN(approvedDealsPercent) ? '-' : `${approvedDealsPercent}%`,
        },
        nonFunded: {
          label: '% Created (of Non-Funded Deals)',
          value: isNaN(nonFundedDealsPercent) ? '-' : `${nonFundedDealsPercent}%`,
        },
      },
    },
    bestUsers: {
      highestMarkup: {
        label: 'Highest Markup $',
        userFullName: userWithHighestMarkup?.fullName,
        value: formatCurrency(userWithHighestMarkup?.markup, true),
      },
      mostProposalsSent: {
        label: 'Most Proposals Sent',
        value: userWithHighestSentAmount?.total,
        userFullName: userWithHighestSentAmount?.fullName,
      },
      highestAcceptedPercent: {
        label: 'Highest Accepted %',
        userFullName: userWithHighestAcceptedAmount?.fullName,
        value: formatPercent(userWithHighestAcceptedAmount?.total / amountOfAccepted),
      },
      highestAcceptedFlat: {
        label: 'Most Proposals Accepted',
        value: userWithHighestAcceptedAmount?.total,
        userFullName: userWithHighestAcceptedAmount?.fullName,
      },
    },
  };
}
